import firebase from 'firebase/app';
import config from '@/config';
import * as Sentry from '@sentry/browser';
import authAxios from './auth-axios';

export default class AuthService {
  static watchForAuth(callback) {
    return firebase.auth().onAuthStateChanged((user) => {
      callback(user);
    });
  }

  static async getRedirectResult() {
    return firebase
      .auth()
      .getRedirectResult()
      .catch((error) => {
        this.throwCorrectError(error);
      });
  }

  static throwCorrectError(error, origin) {
    Sentry.captureException(error);

    const isDifferentCredential =
      error.code ===
      'auth/account-exists-with-different-credential';
    const isCookie =
      (error.code =
        'auth/operation-not-supported-in-this-environment') &&
      !origin;

    if (isDifferentCredential) {
      const isGitHub =
        error.credential &&
        error.credential.providerId ===
          firebase.auth.GithubAuthProvider.PROVIDER_ID;

      throw new Error(
        `Your email is already linked to the ${
          isGitHub ? 'Google' : 'GitHub'
        } provider. Please sign in with ${
          isGitHub ? 'Google' : 'GitHub'
        }.`,
      );
    } else if (isCookie) {
      throw new Error(
        'Please make sure you have cookies enabled in your browser.',
      );
    } else if (origin === 'sign-in') {
      throw new Error(
        `Sorry, we don't recognize your credentials.`,
      );
    } else {
      throw new Error(
        'Ops, an error ocurred. Please try again later.',
      );
    }
  }

  static async signinWithGoogle(rememberMe) {
    const persistence = rememberMe
      ? firebase.auth.Auth.Persistence.LOCAL
      : firebase.auth.Auth.Persistence.SESSION;

    await firebase.auth().setPersistence(persistence);

    const provider = new firebase.auth.GoogleAuthProvider();
    provider.addScope(
      'https://www.googleapis.com/auth/userinfo.email',
    );
    return firebase.auth().signInWithRedirect(provider);
  }

  static async signinWithGitHub(rememberMe) {
    const persistence = rememberMe
      ? firebase.auth.Auth.Persistence.LOCAL
      : firebase.auth.Auth.Persistence.SESSION;

    await firebase.auth().setPersistence(persistence);

    const provider = new firebase.auth.GithubAuthProvider();
    provider.addScope('user:email');
    return firebase.auth().signInWithRedirect(provider);
  }

  static async sendPasswordResetEmail(email) {
    return firebase.auth().sendPasswordResetEmail(email, {
      url: config.CLIENT_APP_URL,
    });
  }

  static async signinWithEmailAndPassword(
    email,
    password,
    rememberMe = false,
  ) {
    try {
      const persistence = rememberMe
        ? firebase.auth.Auth.Persistence.LOCAL
        : firebase.auth.Auth.Persistence.SESSION;

      await firebase.auth().setPersistence(persistence);

      return await firebase
        .auth()
        .signInWithEmailAndPassword(email, password);
    } catch (error) {
      this.throwCorrectError(error, 'sign-in');
    }
  }

  static async triggerUserBackendCreation() {
    const axios = await authAxios();
    await axios.get(`/user/me`);
  }

  static async deleteAccount() {
    const axios = await authAxios();
    await axios.delete(`/user/me`);
  }

  static async fetchCurrentUser() {
    let snapshot = await firebase
      .firestore()
      .doc(`users/${firebase.auth().currentUser.uid}`)
      .get();

    if (!snapshot.exists) {
      await this.triggerUserBackendCreation();
    }

    snapshot = await firebase
      .firestore()
      .doc(`users/${firebase.auth().currentUser.uid}`)
      .get();

    return {
      uid: firebase.auth().currentUser.uid,
      ...snapshot.data(),
    };
  }

  static signOut() {
    return firebase.auth().signOut();
  }

  static token() {
    if (
      !firebase.auth().currentUser ||
      !firebase.auth().currentUser.uid
    ) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .currentUser.getIdToken(true)
        .then((idToken) => {
          return resolve(idToken);
        })
        .catch((error) => reject(error));
    });
  }
}
